import { Alert, Label, Radio, Tag, Text, Tooltip } from '@nimbus-ds/components'
import type { RiskLevel } from '@/types'

/**
 * Risk level control (docs/specs/RISK-LEVELS-COPY.md) — "¿qué tan seguro querés estar antes
 * de decidir?". One jargon-free choice with three stops; the confidence-threshold /
 * expected-loss / wait-vs-act tradeoff stays under the hood.
 *
 * Guardrail: `agile` is offered only for reversible changes (the "worst case is a tie"
 * promise holds only when you can undo it). With `hardToRevert`, agile is disabled — but if
 * it was already selected we keep the selection and show the soft warning instead of
 * silently overriding the user's choice.
 */

interface RiskOption {
  level: RiskLevel
  icon: string
  title: string
  /** The scannable posture verb — second line, carries the meaning on its own. */
  verb: string
  description: string
  ideal: string
}

const OPTIONS: RiskOption[] = [
  {
    level: 'safe',
    icon: '🛡️',
    title: 'Seguro',
    verb: 'Esperar a un ganador claro',
    description:
      'Te avisamos solo cuando el resultado es sólido. Preferís esperar antes que arriesgar una decisión equivocada.',
    ideal: 'Ideal para cambios grandes o difíciles de revertir.',
  },
  {
    level: 'balanced',
    icon: '⚖️',
    title: 'Equilibrado',
    verb: 'Decidir cuando está razonablemente claro',
    description:
      'Te avisamos cuando el resultado es bastante claro. Un balance sensato entre rapidez y confianza.',
    ideal: 'Recomendado para la mayoría de los casos.',
  },
  {
    level: 'agile',
    icon: '⚡',
    title: 'Ágil',
    verb: 'Actuar sobre la tendencia temprana',
    description:
      'Vas con el que va adelante apenas se despega. Si más tarde termina empatando, casi no perdés nada: solo marcamos resultados que ya se ven distintos.',
    ideal: 'Ideal para tiendas con poco tráfico, o cambios rápidos y fáciles de revertir.',
  },
]

/** Verdict-annotation copy — the posture that called a result (used next to "X Gana"). */
export const RISK_VERDICT_ANNOTATION: Record<RiskLevel, string> = {
  agile: 'Resultado temprano · criterio Ágil',
  balanced: 'Resultado con confianza razonable',
  safe: 'Resultado sólido',
}

const AGILE_REASSURANCE =
  'Solo te marcamos un ganador cuando la diferencia ya es visible. Si con más datos termina empatando, volvés a un punto parecido al de partida — no a una pérdida.'

const AGILE_DISABLED_TOOLTIP =
  'No disponible para cambios difíciles de revertir. Acá conviene estar seguro antes de aplicar.'

const AGILE_HARD_TO_REVERT_WARNING =
  'Ojo: este cambio es difícil de deshacer. Con el modo Ágil podrías aplicar algo que después no conviene.'

interface RiskLevelSelectorProps {
  value: RiskLevel
  onChange: (level: RiskLevel) => void
  /**
   * The context-driven recommendation (not a raw preference): hard-to-revert → 'safe';
   * low-traffic store default → 'agile'; otherwise 'balanced'. Shows the [Recomendado] tag
   * and its why-line.
   */
  recommended?: RiskLevel
  /** Hard-to-revert change: disables 'agile' (tooltip) or soft-warns if already selected. */
  hardToRevert?: boolean
  /** Unique radio-group name when the control appears more than once per page. */
  name?: string
  /** Hide the built-in header (when the host form supplies its own label). */
  hideHeader?: boolean
}

export function RiskLevelSelector({
  value,
  onChange,
  recommended = 'balanced',
  hardToRevert = false,
  name = 'risk-level',
  hideHeader = false,
}: RiskLevelSelectorProps) {
  const whyLine =
    recommended === 'agile'
      ? 'Con tu volumen de tráfico, esperar una certeza total puede tardar meses. Te conviene actuar sobre señales lo bastante fuertes.'
      : recommended === 'safe' && hardToRevert
        ? 'Este cambio no es fácil de deshacer, así que conviene esperar un resultado sólido antes de aplicarlo.'
        : 'Un punto de partida sensato. Ajustalo según cuánto te urja decidir.'

  return (
    <div className="space-y-2">
      {!hideHeader && (
        <div className="space-y-1">
          <Label>¿Qué tan seguro querés estar antes de decidir?</Label>
          <Text fontSize="caption">Más seguridad, más esperás. Menos seguridad, decidís antes.</Text>
        </div>
      )}

      <div className="space-y-2">
        {OPTIONS.map((opt) => {
          const selected = value === opt.level
          const isRecommended = recommended === opt.level
          const disabled = opt.level === 'agile' && hardToRevert && !selected

          const card = (
            <div
              key={opt.level}
              onClick={() => !disabled && onChange(opt.level)}
              className={`rounded-md border p-3 transition-colors ${
                selected ? 'border-blue-600 bg-blue-50/50' : 'border-neutral-200'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-blue-400'}`}
            >
              <div className="flex items-start gap-3">
                <Radio
                  name={name}
                  id={`${name}-${opt.level}`}
                  value={opt.level}
                  checked={selected}
                  disabled={disabled}
                  onChange={() => !disabled && onChange(opt.level)}
                />
                <div className="space-y-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <Text as="span" fontWeight="medium" color="neutral-textHigh">
                      {opt.icon} {opt.title}
                    </Text>
                    {isRecommended && (
                      <span className="shrink-0 whitespace-nowrap">
                        <Tag appearance="primary">Recomendado</Tag>
                      </span>
                    )}
                  </div>
                  {/* The scannable line: the posture verb carries the meaning on its own. */}
                  <Text fontWeight="medium" color="neutral-textHigh">
                    {opt.verb}
                  </Text>
                  <Text fontSize="caption">{opt.description}</Text>
                  <Text fontSize="caption" fontWeight="medium">
                    {opt.ideal}
                  </Text>
                  {opt.level === 'agile' && !disabled && (
                    <Tooltip content={AGILE_REASSURANCE} position="bottom">
                      <Text as="span" fontSize="caption" color="primary-interactive">
                        ¿Por qué es seguro apurarse?
                      </Text>
                    </Tooltip>
                  )}
                </div>
              </div>
            </div>
          )

          return disabled ? (
            <Tooltip key={opt.level} content={AGILE_DISABLED_TOOLTIP} position="bottom">
              {card}
            </Tooltip>
          ) : (
            card
          )
        })}
      </div>

      {/* The why-line under the pre-selected recommendation. */}
      <Text fontSize="caption">{whyLine}</Text>

      {/* Soft warning: agile stayed selected on a change flagged hard-to-revert. */}
      {value === 'agile' && hardToRevert && (
        <Alert appearance="warning" title="Modo Ágil con un cambio difícil de revertir">
          {AGILE_HARD_TO_REVERT_WARNING}
        </Alert>
      )}
    </div>
  )
}
